"use client";

import React from "react";
import { Fingerprint, Plus, Trash2, AlertCircle, X } from "lucide-react";
import {
  ENGINEER_SYSTEMS,
  describeValueIssue,
  fieldsForSystem,
  findFieldDef,
  systemDisplay,
  type TechnicalIdentifier,
} from "@wtc/technical-identifiers";

/** Draft of the entry being added in the panel. */
export interface TechnicalIdentifierDraft {
  system: string;
  label: string;
  value: string;
  notes: string;
}

interface TechnicalIdentifiersEditorPanelProps {
  showEditorDirectly: boolean;
  entries: TechnicalIdentifier[];
  draft: TechnicalIdentifierDraft;
  onDraftChange: (patch: Partial<TechnicalIdentifierDraft>) => void;
  validationError: string | null;
  /**
   * True while the user's own save mutation is in flight.
   *
   * Named `isSaving`, not `isPending`: the flag comes from
   * `updateCompany.isPending`, a MUTATION the user triggered, never a background
   * query. TYPING_FOCUS_STABILITY only forbids the latter — disabling a focused
   * field blurs it and drops in-flight characters, which is fine for the moment
   * after Save and not fine on a refetch. The old name did not say which it was.
   */
  isSaving: boolean;
  maxItems: number;
  handleAdd: () => void;
  handleRemove: (system: string, label: string) => void;
  handleClose: () => void;
}

const SYSTEMS_DATALIST_ID = "technical-identifier-systems";
const LABELS_DATALIST_ID = "technical-identifier-labels";

export function TechnicalIdentifiersEditorPanel({
  showEditorDirectly,
  entries,
  draft,
  onDraftChange,
  validationError,
  isSaving,
  maxItems,
  handleAdd,
  handleRemove,
  handleClose,
}: TechnicalIdentifiersEditorPanelProps) {
  const canAddMore = entries.length < maxItems;
  const draftReady = !!draft.system.trim() && !!draft.label.trim() && !!draft.value.trim();

  // Labels the chosen system canonically carries, and the field the typed label
  // resolves to — these drive the label suggestions, the value placeholder, and
  // the advisory shape warning below.
  const systemFields = fieldsForSystem(draft.system);
  const draftField = findFieldDef(draft.system, draft.label);
  const valueWarning = draft.value.trim()
    ? describeValueIssue(draft.system, draft.label, draft.value)
    : null;

  return (
    <div className="space-y-4 p-4">
      {!showEditorDirectly && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
            <Fingerprint className="h-4 w-4" />
            Technical Identifiers
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Staff-only. System identifiers support engineers use to service this client (M365
        tenant, Azure subscription, AWS account…). Not for secrets, passwords, or API keys.
      </p>

      {entries.length > 0 ? (
        <div className="space-y-2">
          <div className={showEditorDirectly ? "space-y-2" : "space-y-2 max-h-56 overflow-y-auto"}>
            {entries.map((entry) => (
              <div
                key={`${entry.system}::${entry.label}`}
                className="flex items-start justify-between gap-2 bg-card-50 rounded-lg p-3 border border-gray-200 hover:border-gray-300 transition-colors dark:border-gray-700"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {/* Display name, not the raw slug — `microsoft_365` is a
                        storage detail, "Microsoft 365" is what the engineer
                        recognizes. `title` keeps the slug discoverable. */}
                    <span
                      title={entry.system}
                      className="inline-flex items-center rounded bg-blue-100 text-blue-800 text-[10px] font-medium px-1.5 py-0.5 dark:bg-blue-900/40 dark:text-blue-200"
                    >
                      {systemDisplay(entry.system)}
                    </span>
                    <span className="text-xs font-medium text-card-foreground truncate">
                      {entry.label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-mono text-card-foreground break-all">
                    {entry.value}
                  </p>
                  {entry.notes && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 break-words">
                      {entry.notes}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(entry.system, entry.label)}
                  disabled={isSaving}
                  className="text-red-600 hover:text-red-700 disabled:opacity-50 flex-shrink-0"
                  title="Remove identifier"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-3">
          <p className="text-sm text-gray-500">No technical identifiers yet</p>
          <p className="text-xs text-gray-400 mt-1">Add one to get started</p>
        </div>
      )}

      {canAddMore && (
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Add identifier</p>
          {/* Suggestions, not a closed <select>: an engineer must always be able
              to record a system we have not catalogued yet. The option VALUE is
              the slug that gets stored; the label is what the engineer reads. */}
          <datalist id={SYSTEMS_DATALIST_ID}>
            {ENGINEER_SYSTEMS.map((system) => (
              <option key={system.slug} value={system.slug} label={system.display} />
            ))}
          </datalist>
          <datalist id={LABELS_DATALIST_ID}>
            {systemFields.map((field) => (
              <option key={field.label} value={field.label} label={field.description} />
            ))}
          </datalist>
          <div className="grid grid-cols-2 gap-2">
            <input
              list={SYSTEMS_DATALIST_ID}
              value={draft.system}
              onChange={(e) => onDraftChange({ system: e.target.value })}
              placeholder="System (e.g. microsoft_365)"
              disabled={isSaving}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
            />
            <input
              list={LABELS_DATALIST_ID}
              value={draft.label}
              onChange={(e) => onDraftChange({ label: e.target.value })}
              placeholder={
                systemFields[0] ? `Label (e.g. ${systemFields[0].label})` : "Label (e.g. Tenant ID)"
              }
              disabled={isSaving}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
            />
          </div>
          <input
            value={draft.value}
            onChange={(e) => onDraftChange({ value: e.target.value })}
            placeholder={
              draftField?.example ? `Value (e.g. ${draftField.example})` : "Value (e.g. 8f9a1b2c-…)"
            }
            disabled={isSaving}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
          />
          {draftField && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{draftField.description}</p>
          )}
          <div className="flex gap-2">
            <input
              value={draft.notes}
              onChange={(e) => onDraftChange({ notes: e.target.value })}
              placeholder="Notes (optional)"
              disabled={isSaving}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-card-100 dark:border-gray-700"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={isSaving || !draftReady}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-card-300 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          {/* A shape mismatch is a NUDGE, never a block — Add stays enabled. A
              vendor that emits an id in a shape we did not anticipate is our
              gap, not the engineer's. */}
          {valueWarning && !validationError && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 text-xs">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span>{valueWarning}</span>
            </div>
          )}
          {validationError && (
            <div className="flex items-center gap-2 text-red-600 text-xs">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span>{validationError}</span>
            </div>
          )}
          <p className="text-xs text-gray-500">{maxItems - entries.length} more allowed</p>
        </div>
      )}
    </div>
  );
}
